import React from 'react';
import styled from 'styled-components';
import LandingPageHeader from '../../components/LandingPageHeader/LandingPageHeader';
import squiggle from '../../resources/images/sqiggly.svg';
import { RedButton } from '../../components/Button';
import { formatDateToMMDD } from '../../utils/date-conversions';
import { Pages } from '../../App';
import BigNumber from 'bignumber.js';

interface Props {
	schoolName: string;
	startDate: Date;
	endDate: Date;
	fairId: string;
	taxStatus: string;
	taxRate: BigNumber;
	totalSales: number;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const LandingPageContent = ({ schoolName, startDate, endDate, fairId, taxStatus, taxRate, totalSales, setCurrentPage }: Props) => {
	const formattedStartDate = formatDateToMMDD(startDate);
	const formattedEndDate = formatDateToMMDD(endDate);
	const formattedTaxStatus = taxStatus === 'OF' ? 'On File' : 'Not on File';

	const formatter = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
	});

	return (
		<>
			<LandingPageHeader />
			<LandingPageContainer>
				<Heading>
					Book Fair <br /> Payment Portal
				</Heading>
				<LandingPageWrapper>
					<SubHeading>{schoolName}</SubHeading>
					<FairDatesLabel>
						Fair dates: {formattedStartDate} - {formattedEndDate}
					</FairDatesLabel>
					<FairDetailsDiv>
						<PaymentPortalCopy>The Payment Portal is available from 5 days before the Fair to 5 days after the fair ends.</PaymentPortalCopy>
						<FairDetailsLabels>Book Fair ID: {fairId}</FairDetailsLabels>
						<FairDetailsLabels>Tax Certificate Status: {formattedTaxStatus}</FairDetailsLabels>
						<FairDetailsLabels>Tax Rate: {taxRate.toNumber()}%</FairDetailsLabels>
					</FairDetailsDiv>
					<BootomDiv>
						<img src={squiggle} alt="squiggle" />
						<TotalPaymentDiv>
							<TotalPaymentLabel>
								Total Payment Portal sales <br /> (less tax and Scholastic Dollars)
							</TotalPaymentLabel>
							<TotalPaymentAmount>{formatter.format(totalSales)}</TotalPaymentAmount>
						</TotalPaymentDiv>
						<StyledRedButton
							handleClick={() => {
								window._satellite.track('landing-page-continue');
								setCurrentPage(Pages.NewPurchase);
							}}
						>
							CONTINUE
						</StyledRedButton>
					</BootomDiv>
				</LandingPageWrapper>
			</LandingPageContainer>
		</>
	);
};

const LandingPageContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const LandingPageWrapper = styled.div`
	display: flex;
	max-width: 320px;
	flex-direction: column;
	justify-content: center;
	padding: 10px 20px 32px 20px;
	box-sizing: border-box;
	gap: 10px;
`;

const Heading = styled.h1`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 120px;
	font-size: 50px;
	font-style: normal;
	font-weight: 400;
	line-height: 47px;
	text-align: center;
	color: #b30042;
	margin: 32px 0 0 0;
	@media (max-width: 720px) {
		font-size: 44px;
		line-height: 42px;
	}
	@media (max-width: 360px) {
		font-size: 36px;
		line-height: 37px;
	}
`;

const SubHeading = styled.h2`
	font-size: 24px;
	font-style: normal;
	font-weight: 400;
	line-height: 28px;
	text-align: center;
	color: #333;
	margin: 0px;
`;

const FairDatesLabel = styled(SubHeading)`
	margin: -2px 0 0 0;
`;

const FairDetailsDiv = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	align-items: center;
`;

const FairDetailsLabels = styled.p`
	font-size: 13px;
	font-style: normal;
	font-weight: 400;
	line-height: 15px;
	color: #333;
	margin: 0;
	text-align: center;
`;

const PaymentPortalCopy = styled(FairDetailsLabels)`
	font-size: 11px;
	line-height: 12px;
	margin: -2px 8px 0;
`;

const BootomDiv = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 24px;
`;

const TotalPaymentDiv = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
`;

const TotalPaymentLabel = styled(FairDetailsLabels)`
	text-align: left;
	width: 118px;
`;

const TotalPaymentAmount = styled(SubHeading)`
	text-align: right;
	flex: 1 0 0;
	width: 141px;
`;

const StyledRedButton = styled(RedButton)`
	width: 100%;
`;

export default LandingPageContent;
